import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CodeAIType, CodeAIMode, CodeAIPreset, CodeAIResponse, CSS_PRESETS, SCRIPT_PRESETS } from '../../models/code-ai/code-ai.model';
import { CSSAIService } from '../../services/api/css-ai/css-ai.service';
import { ScriptAIService } from '../../services/api/script-ai/script-ai.service';

/**
 * Interface for code history entry
 */
interface CodeHistoryEntry {
  code: string;
  timestamp: Date;
  prompt: string;
}

/**
 * Shared component for AI-powered code generation (CSS and JavaScript)
 * Can be used as a modal or embedded panel
 */
@Component({
  selector: 'app-code-ai-assistant',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './code-ai-assistant.component.html',
  styleUrls: ['./code-ai-assistant.component.scss']
})
export class CodeAIAssistantComponent implements OnInit {
  /** Type of code to generate */
  @Input() type: CodeAIType = 'css';
  
  /** Preference Page ID (backend will generate HTML from this) */
  @Input() preferencePageId: string = '';
  
  /** Current HTML context from page builder (deprecated - use preferencePageId) */
  @Input() htmlContext: string = '';
  
  /** Existing code in the section */
  @Input() existingCode: string = '';
  
  /** Optional CSS context (useful for script generation) */
  @Input() cssContext?: string;
  
  /** Whether to show the modal */
  @Input() isOpen: boolean = false;
  
  /** Emits when code is generated and accepted */
  @Output() codeGenerated = new EventEmitter<string>();
  
  /** Emits when modal is closed */
  @Output() modalClosed = new EventEmitter<void>();

  // Component state
  userPrompt: string = '';
  generatedCode: string = '';
  explanation: string = '';
  isGenerating: boolean = false;
  showPreview: boolean = false;
  generationMode: CodeAIMode = 'replace';
  error: string = '';
  errorType: 'error' | 'warning' | 'info' = 'error';
  copySuccess: boolean = false;
  
  // Edit mode properties (now always automatic)
  showCurrentCode: boolean = false;
  codeHistory: CodeHistoryEntry[] = [];
  maxHistorySize: number = 10;
  
  // Presets
  presets: CodeAIPreset[] = [];
  filteredPresets: CodeAIPreset[] = [];
  selectedCategory: string = 'all';
  categories: string[] = [];

  // Quick action prompts (matching preset structure)
  quickActions = [
    { icon: 'fa-magic', label: 'Add Animations', prompt: 'Add smooth animations and transitions' },
    { icon: 'fa-hand-pointer', label: 'Add Hover Effects', prompt: 'Add hover effects to interactive elements' },
    { icon: 'fa-mobile-alt', label: 'Make Responsive', prompt: 'Make the design responsive for mobile devices' },
    { icon: 'fa-universal-access', label: 'Improve Accessibility', prompt: 'Improve accessibility with ARIA labels and focus states' },
    { icon: 'fa-cog', label: 'Optimize Code', prompt: 'Optimize and clean up the code' },
    { icon: 'fa-comment-alt', label: 'Add Comments', prompt: 'Add helpful comments explaining the code' }
  ];

  constructor(
    private cssAIService: CSSAIService,
    private scriptAIService: ScriptAIService
  ) {}

  ngOnInit(): void {
    this.loadPresets();
  }

  /**
   * Check if there is existing code to modify
   */
  hasExistingCode(): boolean {
    return this.existingCode && this.existingCode.trim().length > 0;
  }

  /**
   * Get dynamic placeholder text based on context
   */
  getPlaceholderText(): string {
    if (this.hasExistingCode()) {
      return 'E.g., "Change button colors to green" or "Add hover animations"';
    }
    return 'E.g., "Make all buttons rounded with blue background and white text"';
  }

  /**
   * Add quick action text to prompt
   */
  addQuickAction(actionPrompt: string): void {
    if (this.userPrompt.trim()) {
      this.userPrompt += '\n' + actionPrompt;
    } else {
      this.userPrompt = actionPrompt;
    }
  }

  /**
   * Save current code to history before generating new code
   */
  saveToHistory(code: string, prompt: string): void {
    if (code && code.trim().length > 0) {
      this.codeHistory.push({
        code: code,
        timestamp: new Date(),
        prompt: prompt
      });
      
      // Keep only last N entries
      if (this.codeHistory.length > this.maxHistorySize) {
        this.codeHistory.shift();
      }
    }
  }

  /**
   * Undo last change
   */
  undo(): void {
    if (this.codeHistory.length > 0) {
      const previous = this.codeHistory.pop()!;
      this.generatedCode = previous.code;
      this.showPreview = true;
    }
  }

  /**
   * Check if undo is available
   */
  canUndo(): boolean {
    return this.codeHistory.length > 0;
  }

  /**
   * Load presets based on code type
   */
  loadPresets(): void {
    this.presets = this.type === 'css' ? CSS_PRESETS : SCRIPT_PRESETS;
    this.filteredPresets = this.presets;
    
    // Extract unique categories
    this.categories = ['all', ...new Set(this.presets.map(p => p.category || 'Other'))];
  }

  /**
   * Filter presets by category
   */
  filterByCategory(category: string): void {
    this.selectedCategory = category;
    if (category === 'all') {
      this.filteredPresets = this.presets;
    } else {
      this.filteredPresets = this.presets.filter(p => p.category === category);
    }
  }

  /**
   * Select a preset and populate the prompt
   */
  selectPreset(preset: CodeAIPreset): void {
    this.userPrompt = preset.prompt;
  }

  /**
   * Generate code from AI
   */
  generateCode(): void {
    if (!this.userPrompt.trim()) {
      this.error = 'Please enter a prompt describing what you want to generate.';
      this.errorType = 'error';
      return;
    }

    // Validate we have either preferencePageId or htmlContext
    if (!this.preferencePageId && !this.htmlContext.trim()) {
      this.error = 'Preference page ID is required for code generation.';
      this.errorType = 'error';
      return;
    }

    // Save current generated code to history before generating new code (if in edit mode)
    if (this.hasExistingCode() && this.generatedCode) {
      this.saveToHistory(this.generatedCode, this.userPrompt);
    } else if (this.hasExistingCode() && this.codeHistory.length === 0) {
      // Save original code on first edit
      this.saveToHistory(this.existingCode, 'Original code');
    }

    this.isGenerating = true;
    this.error = '';
    this.errorType = 'error';
    this.generatedCode = '';
    this.explanation = '';

    // Build enhanced prompt with context if there's existing code
    let enhancedPrompt = this.userPrompt;
    let codeToModify = this.existingCode;
    
    if (this.hasExistingCode()) {
      // Use the most recent generated code if available
      if (this.showPreview && this.generatedCode) {
        codeToModify = this.generatedCode;
      }
      
      // IMPROVED: Simpler, more direct prompt that maintains user's intent
      enhancedPrompt = `Modify this ${this.type === 'css' ? 'CSS' : 'JavaScript'} code based on the user's request.

Current code:
\`\`\`${this.type === 'css' ? 'css' : 'javascript'}
${codeToModify}
\`\`\`

User request: ${this.userPrompt}

Important: Make ONLY the changes requested by the user. If the user asks to fix an error, only fix that error. If the user asks to add something, only add that. Keep all other code unchanged.`;
    }

    const request = {
      prompt: enhancedPrompt,
      preferencePageId: this.preferencePageId,
      html: this.htmlContext,
      existingCode: this.hasExistingCode() ? codeToModify : this.existingCode,
      cssContext: this.cssContext,
      existingCSS: this.cssContext,
      mode: this.hasExistingCode() ? 'replace' : this.generationMode
    };

    console.log('Generating code with request:', request);

    const service$ = this.type === 'css' 
      ? this.cssAIService.generateCSS(request)
      : this.scriptAIService.generateScript(request);

    service$.subscribe({
      next: (response: CodeAIResponse) => {
        console.log('Received response:', response);
        this.isGenerating = false;
        
        if (response && response.success) {
          this.generatedCode = response.code || '';
          this.explanation = response.explanation || '';
          this.showPreview = true;
          
          // Show validation messages for scripts with appropriate severity
          if (this.type === 'javascript' && response.validation) {
            // OPTION: Temporarily disable security warnings in development
            const showSecurityWarnings = true; // ? Change to false to disable
            
            if (showSecurityWarnings && response.validation.securityIssues?.length) {
              this.error = `?? Security Note: ${response.validation.securityIssues.join(', ')} - Review code before applying.`;
              this.errorType = 'warning';
            } else if (response.validation.warnings?.length) {
              this.error = `?? Warnings: ${response.validation.warnings.join(', ')}`;
              this.errorType = 'warning';
            } else if (response.validation.syntaxErrors?.length) {
              this.error = `? Syntax Errors: ${response.validation.syntaxErrors.join(', ')}`;
              this.errorType = 'error';
            }
          }
        } else {
          this.error = response?.error || 'Failed to generate code. Please try again.';
          this.errorType = 'error';
          console.error('Generation failed:', response);
        }
      },
      error: (err: any) => {
        console.error('Code generation error:', err);
        this.isGenerating = false;
        this.error = err?.error?.message || err?.message || 'An error occurred while generating code. Please try again.';
        this.errorType = 'error';
      }
    });
  }

  /**
   * Accept the generated code and emit to parent
   */
  acceptCode(): void {
    if (this.generatedCode) {
      let finalCode = this.generatedCode;
      
      // If append mode, combine with existing code (only when not in auto-edit mode)
      if (this.generationMode === 'append' && this.existingCode && !this.hasExistingCode()) {
        finalCode = this.existingCode + '\n\n' + this.generatedCode;
      }
      
      // Update existing code for iterative editing
      if (this.hasExistingCode()) {
        this.existingCode = finalCode;
      }
      
      this.codeGenerated.emit(finalCode);
      
      // Close modal after applying code
      this.closeModal();
    }
  }

  // Animation state
  isClosing: boolean = false;

  /**
   * Close the modal with animation
   */
  closeModal(): void {
    if (this.isClosing) return;
    
    this.isClosing = true;
    
    // Wait for animation to finish (400ms matches CSS)
    setTimeout(() => {
      this.isOpen = false;
      this.isClosing = false;
      this.resetForm();
      this.modalClosed.emit();
    }, 400);
  }

  /**
   * Reset preview state only (for iterative edits)
   */
  resetPreview(): void {
    this.userPrompt = '';
    this.generatedCode = '';
    this.explanation = '';
    this.error = '';
    this.errorType = 'error';
    this.showPreview = false;
  }

  /**
   * Reset form state
   */
  resetForm(): void {
    this.userPrompt = '';
    this.generatedCode = '';
    this.explanation = '';
    this.error = '';
    this.errorType = 'error';
    this.showPreview = false;
    this.generationMode = 'replace';
    this.showCurrentCode = false;
    this.codeHistory = []; // Reset history when closing modal
  }

  /**
   * Copy generated code to clipboard
   */
  copyToClipboard(): void {
    if (this.generatedCode) {
      navigator.clipboard.writeText(this.generatedCode).then(() => {
        this.copySuccess = true;
        setTimeout(() => {
          this.copySuccess = false;
        }, 2000);
      }).catch(err => {
        console.error('Failed to copy code:', err);
      });
    }
  }

  /**
   * Get title based on code type
   */
  getTitle(): string {
    return this.type === 'css' ? 'CSS AI Generator' : 'JavaScript AI Generator';
  }

  /**
   * Get icon based on code type
   */
  getIcon(): string {
    return this.type === 'css' ? 'fa-palette' : 'fa-code';
  }
}
